/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: CidModifiersFormView.tsx
 🗓️ Created: 2025-01-28
 🧩 Description:
    Form view for creating and editing CID Modifiers.
    Manages CID modifier configuration with General and Advanced Settings sections.

 🔧 Notes:
    - Domain-scoped data
    - Supports JSONB settings for advanced FreeSWITCH features
    - Single page with two sections: General and Settings

 🔁 Last Updated: 2025-01-28 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { fetchCidModifier, createCidModifier, updateCidModifier, fetchCidModifiers } from './api/cidModifierApi';
import { CidModifierFormState, CidModifier } from './types';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select-shadcn';
import Toggle from '../../components/ui/Toggle';
import { Phone, Plus, Settings, Search, Copy, X } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts';
import { Label } from '../../components/ui/label';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { FormBox } from '../../components/ui/FormBox';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { deleteCidModifierById, navigateToCidModifierDuplicate } from './cidModifiersActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { CidModifierGeneralSection } from './components/CidModifierGeneralSection';

export default function CidModifiersFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError, showInfo } = useNotifications();

  const [isLoading, setIsLoading] = useState(Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const lastSaveTimestamp = useRef<number>(0);

  // Quick Search state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; name: string; description?: string | null }>>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Helper function to generate context from name
  const generateContext = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')  // Remove special characters
      .replace(/\s+/g, '_')          // Replace spaces with underscore
      .replace(/_+/g, '_')           // Replace multiple underscores with single
      .replace(/^_|_$/g, '')         // Remove leading/trailing underscores
      .substring(0, 50);             // Limit to 50 chars
  };

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    source: 'static' as 'static' | 'database' | 'http',
    enabled: true,
    // Static settings
    staticSettings: {
      cidNumber: {
        skip: null as number | null,
        length: null as number | null,
        prepend: '',
        append: ''
      },
      cidName: {
        prepend: '',
        append: '',
        replaceWith: '',
        forceE164: false,
        stripNonDigits: false,
        blockNameIfEmpty: false
      }
    },
    // Database settings
    databaseSettings: {
      database: '',
      query: '',
      host: '',
      username: '',
      password: ''
    },
    // HTTP settings
    httpSettings: {
      url: '',
      authUser: '',
      authPassword: '',
      timeout: 5,
      failoverBehavior: 'keepOriginal' as 'keepOriginal' | 'replaceWithFallback' | 'keepOnlyNumber' | 'keepOnlyName' | 'rejectCall',
      retries: 0
    },
    settings: {
      normalize_format: 'E164',
      strip_digits: null as number | null,
      append_position: 'end',
      prefix_if_missing: true,
      force_uppercase_name: false,
      regex_replace: null as { match: string; replace: string } | null,
      log_changes: true,
      event_webhook: ''
    } as Record<string, any>
  });

  // Simulation state
  const [simulation, setSimulation] = useState({
    testNumber: '1 (503) 558-1122',
    testName: 'John Smith',
    modifiedNumber: '',
    modifiedName: ''
  });

  // Track original data to allow reset
  const [originalData, setOriginalData] = useState({
    name: '',
    source: 'static' as 'static' | 'database' | 'http',
    enabled: true,
    staticSettings: {
      cidNumber: { skip: null, length: null, prepend: '', append: '' },
      cidName: { prepend: '', append: '', replaceWith: '', forceE164: false, stripNonDigits: false, blockNameIfEmpty: false }
    },
    databaseSettings: { database: '', query: '', host: '', username: '', password: '' },
    httpSettings: { url: '', authUser: '', authPassword: '', timeout: 5, failoverBehavior: 'keepOriginal' as 'keepOriginal' | 'replaceWithFallback' | 'keepOnlyNumber' | 'keepOnlyName' | 'rejectCall', retries: 0 },
    settings: {} as Record<string, any>
  });

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Refs for form reset tracking
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Load CID modifier if editing or duplicating
  useEffect(() => {
    if (isEditMode && id) {
      loadModifier(id, false);
    } else if (duplicateId) {
      loadModifier(duplicateId, true);
    }
  }, [id, duplicateId, isEditMode]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      !duplicateId &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData({
        name: '',
        source: 'static' as 'static' | 'database' | 'http',
        enabled: true,
        staticSettings: {
          cidNumber: { skip: null, length: null, prepend: '', append: '' },
          cidName: { prepend: '', append: '', replaceWith: '', forceE164: false, stripNonDigits: false, blockNameIfEmpty: false }
        },
        databaseSettings: { database: '', query: '', host: '', username: '', password: '' },
        httpSettings: { url: '', authUser: '', authPassword: '', timeout: 5, failoverBehavior: 'keepOriginal' as 'keepOriginal' | 'replaceWithFallback' | 'keepOnlyNumber' | 'keepOnlyName' | 'rejectCall', retries: 0 },
        settings: {
          normalize_format: 'E164',
          strip_digits: null as number | null,
          append_position: 'end',
          prefix_if_missing: true,
          force_uppercase_name: false,
          regex_replace: null as { match: string; replace: string } | null,
          log_changes: true,
          event_webhook: ''
        } as Record<string, any>
      });
      setOriginalData({
        name: '',
        source: 'static' as 'static' | 'database' | 'http',
        enabled: true,
        staticSettings: {
          cidNumber: { skip: null, length: null, prepend: '', append: '' },
          cidName: { prepend: '', append: '', replaceWith: '', forceE164: false, stripNonDigits: false, blockNameIfEmpty: false }
        },
        databaseSettings: { database: '', query: '', host: '', username: '', password: '' },
        httpSettings: { url: '', authUser: '', authPassword: '', timeout: 5, failoverBehavior: 'keepOriginal' as 'keepOriginal' | 'replaceWithFallback' | 'keepOnlyNumber' | 'keepOnlyName' | 'rejectCall', retries: 0 },
        settings: {} as Record<string, any>
      });
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, duplicateId, resetParam, searchParams]);

  const loadModifier = async (modifierId: string, duplicate: boolean = false) => {
    if (!modifierId || modifierId === 'new') return;

    setIsLoading(true);
    try {
      const modifier = await fetchCidModifier(modifierId);

      if (modifier) {
        const settings = modifier.settings || {};

        const loadedData = {
          name: modifier.name || '',
          source: modifier.source || 'static',
          enabled: modifier.enabled ?? true,
          staticSettings: {
            cidNumber: {
              skip: settings.staticSettings?.cidNumber?.skip ?? null,
              length: settings.staticSettings?.cidNumber?.length ?? null,
              prepend: settings.staticSettings?.cidNumber?.prepend || '',
              append: settings.staticSettings?.cidNumber?.append || ''
            },
            cidName: {
              prepend: settings.staticSettings?.cidName?.prepend || '',
              append: settings.staticSettings?.cidName?.append || '',
              replaceWith: settings.staticSettings?.cidName?.replaceWith || '',
              forceE164: settings.staticSettings?.cidName?.forceE164 ?? false,
              stripNonDigits: settings.staticSettings?.cidName?.stripNonDigits ?? false,
              blockNameIfEmpty: settings.staticSettings?.cidName?.blockNameIfEmpty ?? false
            }
          },
          databaseSettings: {
            database: settings.databaseSettings?.database || '',
            query: settings.databaseSettings?.query || '',
            host: settings.databaseSettings?.host || '',
            username: settings.databaseSettings?.username || '',
            password: settings.databaseSettings?.password || ''
          },
          httpSettings: {
            url: settings.httpSettings?.url || '',
            authUser: settings.httpSettings?.authUser || '',
            authPassword: settings.httpSettings?.authPassword || '',
            timeout: settings.httpSettings?.timeout ?? 5,
            failoverBehavior: settings.httpSettings?.failoverBehavior || 'keepOriginal',
            retries: settings.httpSettings?.retries ?? 0
          },
          settings: {
            normalize_format: settings.normalize_format || 'E164',
            strip_digits: settings.strip_digits ?? null,
            append_position: settings.append_position || 'end',
            prefix_if_missing: settings.prefix_if_missing ?? true,
            force_uppercase_name: settings.force_uppercase_name ?? false,
            regex_replace: settings.regex_replace || null,
            log_changes: settings.log_changes ?? true,
            event_webhook: settings.event_webhook || ''
          }
        };

        if (duplicate) {
          // Add copy suffix to name
          const copySuffix = t('common:copySuffix');
          loadedData.name = `${loadedData.name} ${copySuffix}`.trim();
          setFormData(loadedData);
          setOriginalData({
            name: '',
            source: 'static' as const,
            enabled: true,
            staticSettings: {
              cidNumber: { skip: null, length: null, prepend: '', append: '' },
              cidName: { prepend: '', append: '', replaceWith: '', forceE164: false, stripNonDigits: false, blockNameIfEmpty: false }
            },
            databaseSettings: { database: '', query: '', host: '', username: '', password: '' },
            httpSettings: { url: '', authUser: '', authPassword: '', timeout: 5, failoverBehavior: 'keepOriginal' as const, retries: 0 },
            settings: {} as Record<string, any>
          });
        } else {
          setFormData(loadedData);
          setOriginalData(loadedData);
        }
      }
    } catch (error) {
      showError(t('cidModifiers.messages.loadError'));
      if (duplicate) {
        navigate('/pbx/incoming-tools/cid-modifiers');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle field changes
  const handleChange = (field: string, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle settings changes
  const handleSettingChange = (field: string, value: string | number | boolean | null) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value
      }
    }));
  };

  // Handle static settings changes
  const handleStaticSettingChange = (section: 'cidNumber' | 'cidName', field: string, value: string | number | boolean | null) => {
    setFormData(prev => ({
      ...prev,
      staticSettings: {
        ...prev.staticSettings,
        [section]: {
          ...prev.staticSettings[section],
          [field]: value
        }
      }
    }));
  };

  // Handle database settings changes
  const handleDatabaseSettingChange = (field: string, value: string | number | boolean | null) => {
    setFormData(prev => ({
      ...prev,
      databaseSettings: {
        ...prev.databaseSettings,
        [field]: value
      }
    }));
  };

  // Handle HTTP settings changes
  const handleHttpSettingChange = (field: string, value: string | number | boolean | null) => {
    setFormData(prev => ({
      ...prev,
      httpSettings: {
        ...prev.httpSettings,
        [field]: value
      }
    }));
  };

  // Auto-update simulation when form data or test values change
  useEffect(() => {
    if (formData.source === 'static') {
      let modifiedNumber = simulation.testNumber;
      let modifiedName = simulation.testName;
      const { cidNumber, cidName } = formData.staticSettings;

      // Apply CID Number modifications
      if (cidNumber.skip !== null || cidNumber.length !== null) {
        let number = modifiedNumber;
        if (cidNumber.skip !== null) {
          if (cidNumber.skip > 0) {
            number = number.substring(cidNumber.skip);
          } else if (cidNumber.skip < 0) {
            number = number.substring(0, number.length + cidNumber.skip);
          }
        }
        if (cidNumber.length !== null) {
          if (cidNumber.length > 0) {
            number = number.substring(0, cidNumber.length);
          } else if (cidNumber.length < 0) {
            number = number.substring(0, number.length + cidNumber.length);
          }
        }
        modifiedNumber = number;
      }
      if (cidNumber.prepend) {
        modifiedNumber = cidNumber.prepend + modifiedNumber;
      }
      if (cidNumber.append) {
        modifiedNumber = modifiedNumber + cidNumber.append;
      }

      // Apply CID Name modifications
      if (cidName.replaceWith) {
        modifiedName = cidName.replaceWith;
      } else {
        if (cidName.prepend) {
          modifiedName = cidName.prepend + modifiedName;
        }
        if (cidName.append) {
          modifiedName = modifiedName + cidName.append;
        }
      }

      // Apply E.164 format if enabled
      if (cidName.forceE164 && !modifiedNumber.startsWith('+')) {
        modifiedNumber = '+' + modifiedNumber;
      }

      // Strip non-digit characters if enabled
      if (cidName.stripNonDigits) {
        modifiedNumber = modifiedNumber.replace(/[^\d+]/g, '');
      }

      setSimulation(prev => {
        // Only update if values actually changed to prevent infinite loops
        if (prev.modifiedNumber !== modifiedNumber || prev.modifiedName !== modifiedName) {
          return {
            ...prev,
            modifiedNumber,
            modifiedName
          };
        }
        return prev;
      });
    }
  }, [
    formData.source,
    formData.staticSettings.cidNumber.skip,
    formData.staticSettings.cidNumber.length,
    formData.staticSettings.cidNumber.prepend,
    formData.staticSettings.cidNumber.append,
    formData.staticSettings.cidName.prepend,
    formData.staticSettings.cidName.append,
    formData.staticSettings.cidName.replaceWith,
    formData.staticSettings.cidName.forceE164,
    formData.staticSettings.cidName.stripNonDigits,
    simulation.testNumber,
    simulation.testName
  ]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = t('cidModifiers.validation.nameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  // Handle form submission
  const handleSubmit = useCallback(async (e?: React.FormEvent): Promise<CidModifier | undefined> => {
    e?.preventDefault();

    if (!validateForm()) {
      showError(t('common.validationError'));
      return undefined;
    }

    setIsSaving(true);
    try {
      // Generate context from name
      const context = generateContext(formData.name.trim());

      const payload = {
        name: formData.name.trim(),
        source: formData.source,
        enabled: formData.enabled,
        settings: {
          ...formData.settings,
          staticSettings: formData.staticSettings,
          databaseSettings: formData.databaseSettings,
          httpSettings: formData.httpSettings
        }
      };

      const result = isEditMode
        ? await updateCidModifier(id!, payload)
        : await createCidModifier(payload);

      // Update original data after successful save
      setOriginalData({ ...formData });

      showSuccess(
        isEditMode
          ? t('cidModifiers.messages.updateSuccess')
          : t('cidModifiers.messages.createSuccess')
      );
      lastSaveTimestamp.current = Date.now();

      // Return saved data for useSaveWithAction
      return result;
    } catch (error) {
      showError(error instanceof Error ? error.message : t('cidModifiers.messages.createError'));
      return undefined;
    } finally {
      setIsSaving(false);
    }
  }, [validateForm, formData, originalData, isEditMode, id, showSuccess, showError, showInfo, t]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as CidModifier;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/cid-modifiers',
    newPath: '/pbx/incoming-tools/cid-modifiers/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // CID Modifiers use ID for navigation
      return `/pbx/incoming-tools/cid-modifiers/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'cidModifiers:saveAndNewData'
  });

  // Handle cancel - navigate back to list
  const handleCancel = useCallback(() => {
    navigate('/pbx/incoming-tools/cid-modifiers');
  }, [navigate]);

  // Handle delete
  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  // Confirm delete
  const confirmDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteCidModifierById(id);
      showSuccess(t('cidModifiers.messages.deleteSuccess'));
      setShowDeleteModal(false);
      navigate('/pbx/incoming-tools/cid-modifiers');
    } catch (error) {
      showError(error instanceof Error ? error.message : t('cidModifiers.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [id, showSuccess, showError, t, navigate]);

  // Handle duplicate from form
  const handleDuplicateFromForm = useCallback(() => {
    if (id && id !== 'new') {
      navigateToCidModifierDuplicate(navigate, id);
    }
  }, [id, navigate]);

  // Load quick list
  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc',
      });

      const result = await fetchCidModifiers(
        {
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortOrder: 'asc',
        },
        undefined,
        'name',
      );
      const items = Array.isArray(result?.data)
        ? (result.data as Array<{ id: number; name: string; description?: string | null }>)
        : [];
      setQuickListItems(items);
    } catch {
      showError(t('cidModifiers.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [showError, t]);

  // Filter quick list
  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.description ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6"><ModuleFormHeader
      icon={<Phone className="h-4 w-4 text-primary" />}
      title={isEditMode
        ? `${t('cidModifiers.editEntry')} - ${formData.name || ''}`
        : t('cidModifiers.createEntry')
      }
      backButtonText="List"
      onBack={handleCancel}
      actions={[
        {
          icon: <Search className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('common.search', 'Search'),
          onClick: () => {
            setShowQuickList(true);
            void loadQuickList();
          },
        },
        ...(isEditMode && !duplicateId
          ? [{
            icon: <Copy className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('common.duplicate'),
            onClick: handleDuplicateFromForm,
          }]
          : []),
        {
          icon: <Plus className="h-5 w-5 text-muted-foreground" />,
          tooltip: t('cidModifiers.actions.add'),
          onClick: () => navigate('/pbx/tools/cid-modifiers/new'),
        },
      ]}
    />

      <div className="mt-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6 pb-20">
            {/* General Section */}
            <FormBox
              title={t('cidModifiers.tabs.general')}
              icon={<Phone className="h-5 w-5" />}
            >
              <CidModifierGeneralSection
                formData={formData}
                errors={errors}
                handleChange={handleChange}
                handleStaticSettingChange={handleStaticSettingChange}
                handleDatabaseSettingChange={handleDatabaseSettingChange}
                handleHttpSettingChange={handleHttpSettingChange}
                t={t}
              />
            </FormBox>

            {/* Simulation Section */}
            {formData.source === 'static' && (
              <FormBox
                title={t('cidModifiers.simulation.title')}
                icon={<Settings className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Left Column */}
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="originalNumber" className="text-sm font-semibold">
                        {t('cidModifiers.simulation.originalNumber')}
                      </Label>
                      <Input
                        id="originalNumber"
                        value={simulation.testNumber}
                        onChange={(e) => setSimulation(prev => ({ ...prev, testNumber: e.target.value }))}
                        placeholder="1 (503) 558-1122"
                        className="mt-1 font-mono font-bold"
                      />
                    </div>
                    <div>
                      <Label htmlFor="modifiedNumber" className="text-sm font-semibold">
                        {t('cidModifiers.simulation.modifiedNumber')}
                      </Label>
                      <Input
                        id="modifiedNumber"
                        value={simulation.modifiedNumber || simulation.testNumber}
                        readOnly
                        className="mt-1 font-mono font-bold bg-gray-50 dark:bg-gray-900"
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="originalName" className="text-sm font-semibold">
                        {t('cidModifiers.simulation.originalName')}
                      </Label>
                      <Input
                        id="originalName"
                        value={simulation.testName}
                        onChange={(e) => setSimulation(prev => ({ ...prev, testName: e.target.value }))}
                        placeholder="John Smith"
                        className="mt-1 font-bold"
                      />
                    </div>
                    <div>
                      <Label htmlFor="modifiedName" className="text-sm font-semibold">
                        {t('cidModifiers.simulation.modifiedName')}
                      </Label>
                      <Input
                        id="modifiedName"
                        value={simulation.modifiedName || simulation.testName}
                        readOnly
                        className="mt-1 font-bold bg-gray-50 dark:bg-gray-900"
                      />
                    </div>
                  </div>
                </div>
              </FormBox>
            )}
          </div>

          <FixedActionBar
            onSaveWithAction={handleSaveWithAction}
            onCancel={handleCancel}
            onDelete={isEditMode ? handleDelete : undefined}
            isEditMode={isEditMode}
            isSaving={isSaving}
            isLoading={isLoading}
            saveText={t('common.save')}
            cancelText={t('common.cancel')}
            deleteText={t('common.delete')}
            useSaveDropdown={true}
            saveDropdownStorageKey="cidModifiers:saveActionPreference"
          />
        </form>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('cidModifiers.messages.deleteTitle')}
        message={t('cidModifiers.messages.confirmDelete', { name: formData.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('cidModifiers.quickList.title', 'CID Modifiers')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/incoming-tools/cid-modifiers/${item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/incoming-tools/cid-modifiers/${item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('cidModifiers.quickList.searchPlaceholder', 'Search by name or description')}
        emptyMessage={t('cidModifiers.quickList.empty', 'No CID modifiers found')}
        headerIcon={<Phone className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}


